import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyContentLocaleToUrl } from '../../utils/contentLocaleQuery';
import { departmentPath, useDepartmentSlug } from '../../utils/departmentRouting';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type PublicCategory = {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  /** Admin-set storefront image (optional). */
  imageUrl?: string | null;
  /** Latest active product image in this category (from API). */
  productImageUrl?: string | null;
};

/** Only used when category has no admin image and no sellable product image. */
const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1544145945-f90425340c7e?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1506368083636-6defb67639a8?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1615485925600-34978c6c15ba?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1596040033229-a9821ebd10d6?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1588165276332-328f90c6c9b5?auto=format&fit=crop&w=800&q=80',
];

function hashSlug(slug: string): number {
  let h = 0;
  for (let i = 0; i < slug.length; i += 1) {
    h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  }
  return h;
}

function resolveMediaUrl(raw: string | null | undefined): string {
  const url = String(raw ?? '').trim();
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `${API_BASE.replace(/\/$/, '')}${url}`;
  return url;
}

/** Prefer admin image, then any product image in the category, then stock photo. */
function categoryCardImageSrc(c: PublicCategory): string {
  const admin = resolveMediaUrl(c.imageUrl);
  if (admin) return admin;
  const fromProduct = resolveMediaUrl(c.productImageUrl);
  if (fromProduct) return fromProduct;
  return FALLBACK_IMAGES[hashSlug(c.slug) % FALLBACK_IMAGES.length];
}

export default function ShopCategoriesStrip() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const departmentSlug = useDepartmentSlug();
  const [categories, setCategories] = React.useState<PublicCategory[]>([]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const url = new URL(`${API_BASE}/catalog/categories`);
        if (departmentSlug) {
          url.searchParams.set('departmentSlug', departmentSlug);
        }
        const res = await fetch(
          applyContentLocaleToUrl(
            url.toString(),
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as PublicCategory[];
        if (!cancelled) setCategories(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [departmentSlug, i18n.language, i18n.resolvedLanguage]);

  if (categories.length === 0) return null;

  return (
    <div className="w-full">
      <div
        className="flex justify-center py-4 sm:py-5"
        style={{ backgroundColor: 'var(--storefront-body-bg, #f4f6f8)' }}
      >
        <button
          type="button"
          onClick={() => navigate(departmentPath(departmentSlug, '/products'))}
          className="rounded-lg px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-105"
          style={{ backgroundColor: 'var(--storefront-nav-accent, #ea580c)' }}
        >
          {t('storefront.shop_categories_view_all', { defaultValue: 'View all' })}
        </button>
      </div>

      <div
        className="w-full px-4 py-10 text-white sm:py-14"
        style={{
          backgroundColor: 'var(--storefront-brand-primary, #059669)',
          backgroundImage:
            'linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(15,23,42,0.2) 100%)',
        }}
      >
        <div className="mx-auto max-w-7xl">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {t('storefront.shop_categories_title', { defaultValue: 'Shop Our Categories' })}
          </h2>

          <div
            className="mt-8 flex gap-4 overflow-x-auto overscroll-x-contain scroll-smooth pb-3 pt-1 sm:gap-5"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() =>
                  navigate(
                    `${departmentPath(departmentSlug, '/products')}?category=${encodeURIComponent(c.slug)}`,
                  )
                }
                className="group flex w-[10.5rem] shrink-0 flex-col overflow-hidden rounded-xl bg-white text-left sm:w-44"
              >
                <div className="relative min-h-[9.5rem] flex-[3] overflow-hidden bg-slate-900/20 sm:min-h-[11rem]">
                  <img
                    src={categoryCardImageSrc(c)}
                    alt=""
                    aria-hidden
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-none flex-[1] items-center justify-between gap-2 border-t border-slate-100 bg-white px-3 py-3 sm:px-4">
                  <span className="min-w-0 text-left text-sm font-semibold leading-snug text-slate-900">{c.name}</span>
                  <span
                    className="shrink-0 text-lg font-light transition group-hover:opacity-90"
                    style={{ color: 'var(--storefront-brand-primary, #059669)' }}
                    aria-hidden
                  >
                    →
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
